import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import gsap from 'gsap';

import sizes from 'styles/sizes';

import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { DATE_RANGE, START_DATE } from 'contexts/CruiseSearchContext/constants';

import { Constrain } from 'components/FiltersModal/components';
import Header from 'components/FiltersModal/Header';
import DateRange from 'components/Calendars/DateRange';
import Months from 'components/Calendars/Months';
import SwitchLabelButton from 'components/buttons/SwitchLabelButton';
import CustomFilters from '../CustomFilters';
import { DATES_FILTER, TYPE_FILTER } from './constants';
import { monthsAvailable, makeInitialRangesFromStartDate } from './utils';

import PropTypes from 'prop-types';
import { getBrandUISettings } from 'lib/utilities/Brands';
import { useFeatureFlags } from '../../../../hooks/LaunchDarkly/useFeatureFlags';

const Wrapper = styled.section`
	display: flex;
	flex-direction: column;
	align-items: center;
	@media (min-width: ${sizes.media.smallMaxWidth}px) {
		margin-bottom: 0;
		min-height: 390px;
	}
`;

const animateIn = keyframes`
  0% {
    transform: scale(0.98) translateX(-7px);
    opacity: 0;
  } 100% {
    transform: scale(1) translateX(0);
    opacity: 1;
  }
`;
const Tabs = styled(SwitchLabelButton)`
	margin: 0 0 50px;
	opacity: 0;
	animation: ${animateIn} 0.27s cubic-bezier(0, 0, 0, 0) forwards;
`;

const TabContentWrapper = styled.div`
	display: flex;
	flex-wrap: nowrap;
	position: relative;
	overflow: hidden;
	width: 100%;
`;

const TabColumnWrapper = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	flex: 0 0 100%;
	display: flex;
	justify-content: center;
	width: 100%;
	overflow: hidden;
`;

/**
 * Dates
 *
 * The Dates filter component listens to/sets the startDate param of the search query.
 * Calendar(s) are controlled by the startDate property in searchSelecting.
 * There are two calendar views (Months, DateRange) in this component.
 */

function Dates({ setModalHeight }) {
	const { updateSearchSelectingFilter, globalization, setAppliedDateFilter, datesSelecting, monthsSelecting, brand, filterOptionsDictionary } =
		useContext(CruiseSearchContext);
	const { showDateRangeFilter } = useFeatureFlags();
	const { uiSettings } = getBrandUISettings(brand);

	/**
	 * Determine initial mobile/desktop
	 */
	let isMobileInitial = false;
	if (process.brower) {
		isMobileInitial = window?.innerWidth <= sizes.media.smallMaxWidth;
	}

	const { filtersDictionary } = globalization?.dictionaries || {};
	const { navCruiseDates, labelSpecialDates, labelMonths, labelDateRange } = filtersDictionary;

	const tabs = {
		months: {
			label: labelMonths,
			appliedDateFilter: START_DATE,
		},
		dateRange: {
			label: labelDateRange,
			appliedDateFilter: DATE_RANGE,
		},
	};

	const TAB_CHANGE_DURATION = 0.5;

	const [isMobile, setIsMobile] = useState(isMobileInitial);
	const [tab, setTab] = useState(tabs.months);
	const [customFilter, setCustomFilter] = useState(null);
	const [selectCustom, setSelectCustom] = useState(null);

	const { custom = [] } = filterOptionsDictionary || {};

	const tabContentRef = useRef();
	const wrapperContentRef = useRef();
	const dateRangeTabRef = useRef();
	const monthsTabRef = useRef();
	const activeTabRef = useRef(); // eslint-disable-line
	const inOutDuration = useRef(0);
	const monthsRef = useRef(null);
	const datesRef = useRef(null);
	const monthsChecked = tab.label === tabs.months.label;

	const updateSearchFilters = (dateFilter, callback) => {
		updateSearchSelectingFilter(START_DATE, dateFilter, callback);
	};

	const onReset = useCallback(() => {
		datesRef?.reset && datesRef.reset();
		monthsRef?.reset && monthsRef.reset();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const onResize = useCallback(() => {
		const isSmol = window?.innerWidth <= sizes.media.smallMaxWidth;
		if (isSmol !== isMobile) {
			setIsMobile(isSmol);
		}
	}, [setIsMobile, isMobile]);

	const getCustomFilters = useCallback(
		(filters) => {
			setCustomFilter(filters);
		},
		[setCustomFilter],
	);

	useEffect(() => {
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [onResize]);

	/**
	 * Add/Remove resize listener
	 */
	useEffect(() => {
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [onResize]);

	/**
	 * Change tabs
	 */
	useEffect(() => {
		if (!tab?.label || !tabContentRef.current) return;
		const prevActiveTabRef = monthsChecked ? dateRangeTabRef : monthsTabRef;
		const activeTabRef = monthsChecked ? monthsTabRef : dateRangeTabRef;
		const prevContentHeight = prevActiveTabRef.current?.clientHeight;
		const contentHeight = activeTabRef.current?.clientHeight;
		const maxHeightOfModal = window.innerHeight - 300;
		const animationHeight = contentHeight < maxHeightOfModal ? contentHeight : maxHeightOfModal;

		/**
		 * Make variable height changes feel more consistent when changing tabs
		 * Animate to/from a max height of the modal height
		 * then set the rest of the (hidden) height
		 */
		if (prevContentHeight > maxHeightOfModal) {
			gsap.set(tabContentRef.current, { height: maxHeightOfModal });
		}

		gsap.to(tabContentRef.current, {
			height: animationHeight,
			duration: inOutDuration.current,
		});

		gsap.fromTo(
			wrapperContentRef.current,
			{
				opacity: 0,
			},
			{
				opacity: 1,
				ease: 'cubic-bezier(0,0,0,0)',
			},
		);

		/**
		 * Animate tab content in/out
		 */

		gsap.fromTo(
			activeTabRef.current,
			{
				opacity: 0,
			},
			{
				zIndex: 1,
				opacity: 1,
				delay: 0.1,
				ease: 'cubic-bezier(0,0,0,0)',
			},
		);
		gsap.fromTo(
			prevActiveTabRef.current,
			{
				opacity: 1,
			},
			{
				zIndex: -3,
				opacity: 0,
				duration: 0.25,
				ease: 'cubic-bezier(0,0,0,0)',
			},
		);

		gsap.set(tabContentRef.current, {
			height: contentHeight,
			delay: inOutDuration.current,
		});

		inOutDuration.current = TAB_CHANGE_DURATION; // So duration is 0 on mount
	}, [tab?.label, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

	/**
	 * SwitchLabelButton logic doesn't make sense
	 * we'll need to update this when we update the button
	 */
	const doToggle = () => {
		if (monthsChecked) {
			setTab(tabs.dateRange);
			setAppliedDateFilter(tabs.dateRange.appliedDateFilter);
			updateSearchFilters(datesSelecting[START_DATE]);
		} else {
			setTab(tabs.months);
			setAppliedDateFilter(tabs.months.appliedDateFilter);
			updateSearchFilters(monthsSelecting[START_DATE]);
		}
	};

	useEffect(() => {
		if (tab === tabs.dateRange) {
			setModalHeight(830);
		} else {
			setModalHeight(700);
		}
	});

	useEffect(() => {
		setAppliedDateFilter(tabs.months.appliedDateFilter);
	}, []);

	return (
		<>
			<Header title={navCruiseDates} onReset={onReset} />
			<Constrain id={DATES_FILTER} className="no-top-padding">
				<Wrapper ref={wrapperContentRef}>
					{showDateRangeFilter && (
						<Tabs
							labelLeft={tabs.months.label}
							labelRight={tabs.dateRange.label}
							checked={monthsChecked} // this doesn't make any sense, need to refactor this component
							onClick={doToggle}
							uiSettings={uiSettings}
						/>
					)}

					{custom?.options?.length > 0 && monthsChecked && (
						<>
							<CustomFilters
								title={labelSpecialDates}
								keyName={TYPE_FILTER}
								updateCustomFilters={getCustomFilters}
								handleClickCustom={(selected) => {
									setSelectCustom(selected);
								}}
							/>
						</>
					)}
					<TabContentWrapper ref={tabContentRef}>
						<TabColumnWrapper ref={dateRangeTabRef}>
							{!monthsChecked && (
								<DateRange
									active={tab === tabs.dateRange}
									datesRef={datesRef}
									isMobile={isMobile}
									makeInitialRangesFromStartDate={makeInitialRangesFromStartDate}
									monthsAvailable={monthsAvailable}
									tabs={tabs}
								/>
							)}
						</TabColumnWrapper>
						<TabColumnWrapper ref={monthsTabRef}>
							<Months
								selectCustom={selectCustom}
								isMobile={isMobile}
								active={tab === tabs.months}
								monthsList={customFilter}
								tabs={tabs}
								monthsRef={monthsRef}
							/>
						</TabColumnWrapper>
					</TabContentWrapper>
				</Wrapper>
			</Constrain>
		</>
	);
}

Dates.propTypes = {
	setModalHeight: PropTypes.func,
};

export default Dates;
